import { polyMeshToGeometry } from 'helpers/geometry'
import useDebounce from 'hooks/use-debounce'
import { useStreamS3ObjectToAppFileCache } from 'hooks/use-stream-s3-object-to-app-file-cache'
import { FC, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'

let clock = new THREE.Clock()

interface Props {
  uuid?: string
  className?: string
  onLoad?: () => void
  endpoint?: string
}

const Turntable: FC<Props> = ({ uuid, className = '', onLoad, endpoint }) => {
  if (!uuid && !endpoint) {
    throw new Error('Either a UUID or an endpoint must be set.')
  }

  const { data, isLoading, error } = useStreamS3ObjectToAppFileCache(
    endpoint ? endpoint : `/api/programs/${uuid}/geometry`,
  )
  const [rotate, setRotate] = useState(false)
  const [geometry, setGeometry] = useState<THREE.BufferGeometry>(null)
  const ref = useRef<HTMLDivElement>(null)
  const scene = new THREE.Scene()

  useEffect(() => {
    if (!data || geometry) {
      return
    }

    setGeometry(polyMeshToGeometry({ polyMesh: data.poly_mesh }))

    if (typeof onLoad === 'function') {
      onLoad()
    }
  }, [data])

  useDebounce(
    () => {
      if (!geometry) {
        return
      }
      setRotate(true)
    },
    [geometry],
    500,
  )

  useEffect(() => {
    if (!geometry) {
      return
    }

    const width: number = ref.current.clientWidth
    const height: number = ref.current.clientHeight

    geometry.computeBoundingBox()
    const sides = geometry.boundingBox.getSize(new THREE.Vector3()).toArray()
    const middle = geometry.boundingBox.getCenter(new THREE.Vector3())

    const largestDimension = Math.max(...sides)
    const smallestDimension = Math.min(...sides)

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    })

    const lightHolder = new THREE.Group()
    lightHolder.add(new THREE.HemisphereLight(0xffffff, 0x000000, 2.5))
    lightHolder.add(new THREE.AmbientLight(0x4d5168, 2))

    scene.add(lightHolder)

    const aspectRatio = width / height

    const frustumSize =
      Math.sqrt(sides.reduce((sum, side) => sum + side ** 2, 0)) * 1.1

    const camera = new THREE.OrthographicCamera(
      (frustumSize * aspectRatio) / -2,
      (frustumSize * aspectRatio) / 2,
      frustumSize / 2,
      frustumSize / -2,
      largestDimension * -1000,
      largestDimension * 1000,
    )

    camera.rotateX(Math.PI * 0.3)
    camera.rotateY(Math.PI * 0.15)
    camera.rotateZ(Math.PI * 0.1)

    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    const material = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      emissive: 0x3c4051,
      side: THREE.DoubleSide,
    })

    const mesh = new THREE.Mesh(geometry.clone(), material)
    scene.add(mesh)

    mesh.geometry.applyMatrix4(
      new THREE.Matrix4().makeTranslation(-middle.x, -middle.y, -middle.z),
    )

    ref.current.appendChild(renderer.domElement)

    const animationLoop = () => {
      const delta = clock.getDelta()
      if (rotate) {
        mesh.rotateZ(Math.PI * -delta * 0.3)
      }

      renderer.render(scene, camera)
      camera.updateProjectionMatrix()
      lightHolder.quaternion.copy(camera.quaternion)
    }

    const turntableRef = ref.current
    renderer.setAnimationLoop(animationLoop)

    return () => {
      renderer?.setAnimationLoop(null)
      turntableRef.removeChild?.(renderer.domElement)
      scene.clear()
    }
  }, [geometry, rotate])

  if (isLoading || !geometry || error) {
    return <></>
  }

  return <div ref={ref} className={className} />
}

export default Turntable
